import { StackedCellText } from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { OPTION_DETAIL_SEPARATOR } from '@/utils/filters';
import { formatNumber } from '@/utils/number';
import type { DcrEntryLine } from '../types/billing.types';
import { DetailLinesTable, type DetailLineColumn } from '@/components/common/DetailLinesTable';

type DcrEntryLineKind = DcrEntryLine['lineKind'];

const LINE_KIND_TITLE: Record<DcrEntryLineKind, string> = {
  specimen: 'Specimens',
  promo: 'Promotion Items',
  contact: 'Contacts',
};

const formatOnBehalf = (line: DcrEntryLine): string => {
  if (!line.isOnBehalf) return 'No';
  return line.onBehalfMobile ?? 'Yes';
};

const formatOptionalNumber = (value: number | null): string | null =>
  value === null ? null : formatNumber(value);

interface PersonCellProps {
  line: DcrEntryLine;
}

function PersonCell({ line }: PersonCellProps) {
  const personDetails = [line.designation, line.subject]
    .filter(Boolean)
    .join(OPTION_DETAIL_SEPARATOR);
  if (!line.personName && !personDetails) return <EmptyValue />;

  return (
    <StackedCellText
      primary={line.personName ?? <EmptyValue />}
      secondary={personDetails || null}
    />
  );
}

const renderPerson = (line: DcrEntryLine) => <PersonCell line={line} />;

const LINE_COLUMNS: Record<DcrEntryLineKind, DetailLineColumn<DcrEntryLine>[]> = {
  specimen: [
    { header: 'Item', render: (line) => line.item },
    { header: 'Qty', isNumeric: true, render: (line) => formatNumber(line.qty ?? 0) },
    { header: 'App. Qty', isNumeric: true, render: (line) => formatNumber(line.approveQty ?? 0) },
    { header: 'Name', render: renderPerson },
    { header: 'Contact', render: (line) => line.contact },
    { header: 'On Behalf', render: formatOnBehalf },
  ],
  promo: [
    { header: 'Item', render: (line) => line.item },
    { header: 'Whom', render: (line) => line.whom },
    { header: 'Qty', isNumeric: true, render: (line) => formatNumber(line.qty ?? 0) },
    { header: 'Teacher Name', render: renderPerson },
    { header: 'Contact', render: (line) => line.contact },
    {
      header: 'Students',
      isNumeric: true,
      render: (line) => formatOptionalNumber(line.totalStudents),
    },
    {
      header: 'Approx Sale',
      isNumeric: true,
      render: (line) => formatOptionalNumber(line.approxSaleQty),
    },
    { header: 'Witness', render: (line) => line.witness },
    { header: 'On Behalf', render: formatOnBehalf },
  ],
  contact: [
    { header: 'Contact No', render: (line) => line.contact },
    { header: 'Type', render: (line) => line.whom },
    { header: 'Name', render: renderPerson },
  ],
};

interface DcrEntryLinesTableProps {
  lineKind: DcrEntryLineKind;
  lines: DcrEntryLine[];
}

export function DcrEntryLinesTable({ lineKind, lines }: DcrEntryLinesTableProps) {
  return (
    <DetailLinesTable
      title={LINE_KIND_TITLE[lineKind]}
      lines={lines}
      columns={LINE_COLUMNS[lineKind]}
      getLineKey={(line) => line.lineId}
    />
  );
}
